import { OrganizationsService } from './organization.service';
import { SpaqFireDb } from './spaq-fire-db.service';
import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AccessCodeGroupInfo, AccessCodeInfo, AccessCodeSearchInfo } from './shared-constants';

import * as moment from 'moment';
import { firstValueFrom, from, Observable, of, throwError, zip } from 'rxjs';
import { first, map, switchMap, take, tap } from 'rxjs/operators';

@Injectable()
export class AccessCodeService {
	constructor(
		private afAuth: AngularFireAuth,
		private db: SpaqFireDb,
		private organizationService: OrganizationsService
	) {}

	add(accessCode: AccessCodeSearchInfo): Observable<any> {
		const userCode = accessCode.userCode.toUpperCase().replace(/[\.\#\[\]]/g, '');
		const companyCode = accessCode.companyCode.toUpperCase().replace(/[\.\#\[\]]/g, '');

		return this.afAuth.user.pipe(
			take(1),
			switchMap(u =>
				this.db.accessCodeEntry$(companyCode, userCode).pipe(
					first(),
					switchMap((res: any) => {
						if (!res) {
							return throwError("Code introuvable. Assurez-vous d'avoir bien saisi vos informations");
						}
						if (res.used === true) {
							return throwError('Ce code est déjà utilisé');
						}
						if (res.single === false) {
							if (res.startAt) {
								if (new Date(res.startAt).getTime() > new Date().getTime()) {
									return throwError("Ce code d'accès sera disponible à partir du " + moment(res.startAt).format('LL'));
								}

								if (new Date(res.endAt).getTime() < new Date().getTime()) {
									return throwError("Ce code d'accès est expiré depuis le " + moment(res.endAt).format('LL'));
								}
							}
						}

						const act = [];
						act.push(this.db.setUserAccessCode(u.uid, { ...res, date: new Date().toISOString() }));

						if (res && res.values && typeof res.values === 'object') {
							act.push(this.db.updateUserSubscriptionCustom(u.uid, res.values));
						}

						return zip(...act);
					})
				)
			)
		);
	}

	delete(): Observable<void> {
		return this.afAuth.user.pipe(
			take(1),
			switchMap(u => from(this.db.removeUserAccessCode(u.uid)))
		);
	}

	get(): Observable<any> {
		return this.afAuth.user.pipe(
			take(1),
			switchMap(u =>
				this.db
					.userAccessCode$<AccessCodeInfo>(u.uid)
					.pipe(switchMap((code: AccessCodeInfo) => (code ? this.getOrganization(code) : of(null))))
			)
		);
	}

	getCodes(companyCode, userCode): Observable<any> {
		return this.db.accessCodeCatalogSnapshot$(companyCode, userCode).pipe(
			first(),
			map(res => ({ id: res.key, ...(res.payload.val() as object) })),
			tap(res => {
				if (!res.id) {
					throw new Error("Code introuvable. Assurez-vous d'avoir bien saisi vos informations");
				}
				if (res.used === true) {
					throw new Error('Ce code est déjà utilisé');
				}
			})
		);
	}

	getCompanyName(companyCode): Observable<any> {
		return this.db.accessCodeCompanyName$(companyCode).pipe(first());
	}

	getOrganization(code: AccessCodeInfo): Observable<any> {
		return this.organizationService.get(code);
	}

	hydrate(): Observable<AccessCodeGroupInfo> {
		return this.afAuth.user.pipe(
			switchMap(u =>
				this.db
					.userAccessCode$<AccessCodeInfo>(u.uid)
					.pipe(
						switchMap((res: any) =>
							!res
								? of(null)
								: this.db.organizationGroup$<AccessCodeGroupInfo>(
										res.organizationCode,
										(res.groupCode && res.groupCode.groupCode) || res.groupCode
									)
						)
					)
			)
		);
	}

	searchAccessCode({ companyCode, userCode }: AccessCodeSearchInfo) {
		companyCode = companyCode.toUpperCase();
		return zip(this.getCodes(companyCode, userCode), this.getCompanyName(companyCode)).pipe(
			map(d => {
				return { ...d[0], companyName: d[1] as AccessCodeSearchInfo };
			})
		);
	}

	resetForOrganizationChange(uid: string) {
		return [
			firstValueFrom(
				this.db.userVehiclesList$(uid).pipe(switchMap(x => this.db.setUserContract(uid, { deleted: true, vehicles: x })))
			),
			this.db.setUserAccessCode(uid, null),
			this.db.setUserSubscriptionCustom(uid, null)
		];
	}
}
